import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Grid,
  TextField,
  IconButton,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { ContentCopy, Edit, Close } from "@mui/icons-material";
import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined';
import { getMessages, updateMessageById } from "../../../services/schedule";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
import CreateNewCampaignMessage from "./CreateNewCampaignMessage";

// AddLibraryModal - supports loading DPDs, fetching messages by DPD,
// editing rows by unique id, copy/delete, and adding new messages.
const AddLibraryModal = ({ open, onClose, onSave, initialLibrary = [] }) => {
  // we'll load all messages on open and allow client-side searching
  const [allRows, setAllRows] = useState([]);
  const [dpdOptions, setDpdOptions] = useState([]);
  const [selectedDpd, setSelectedDpd] = useState("All");
  const [channelOptions, setChannelOptions] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState("All");
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("All");
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState("All");
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("All");
  const [fromDbaOptions, setFromDbaOptions] = useState([]);
  const [selectedFromDba, setSelectedFromDba] = useState("All");
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState(initialLibrary || []);
  const [editingId, setEditingId] = useState(null);
  const [editMessageValue, setEditMessageValue] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Keep rows in sync with initialLibrary prop
  useEffect(() => {
    setRows(initialLibrary || []);
  }, [initialLibrary]);

  // Load messages on open (all messages)
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const msgs = await getMessages();
        const mapped = Array.isArray(msgs)
          ? msgs.map((m) => ({
              // keep original fields where present; map common ones
              dpd: m.dpd || m.DPD || m.typeValue || "",
              message: m.Description || m.message || "",
              id: m.id || m.ID || null,
              templateName: m.TemplateName || m.templateName || "",
              desc_count: m.desc_count != null ? m.desc_count : 0,
              language: m.Language || m.language || m.lang || "",
              ...m,
            }))
          : [];
        if (mounted) {
          setAllRows(mapped);
          setRows(mapped);
          // extract distinct dpd, channel and template values for dropdowns
          const dpds = Array.from(new Set(mapped.map((m) => (m.dpd || "").toString()).filter(Boolean))).sort();
          const channels = Array.from(new Set(mapped.map((m) => (m.Channel || m.channel || m.typeValue || "").toString()).filter(Boolean))).sort();
          const templates = Array.from(new Set(mapped.map((m) => (m.TemplateName || m.templateName || "").toString()).filter(Boolean))).sort();
          const states = Array.from(new Set(mapped.map((m) => (m.State || m.state || "").toString()).filter(Boolean))).sort();
          const languages = Array.from(new Set(mapped.map((m) => (m.Language || m.language || m.lang || "").toString()).filter(Boolean))).sort();
          const fromdbas = Array.from(new Set(mapped.map((m) => (m.FromDBAName || m.fromDbaName || "").toString()).filter(Boolean))).sort();
          setDpdOptions(dpds);
          setChannelOptions(channels);
          setTemplateOptions(templates);
          setStateOptions(states);
          setLanguageOptions(languages);
          setFromDbaOptions(fromdbas);
        }
      } catch (err) {
        console.error("Failed to load messages", err);
        if (mounted)
          setSnackbar({ open: true, message: "Failed to load messages", severity: "error" });
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open]);
  // Add feature removed: no create handlers

  // search removed per request

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: "Copied to clipboard",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to copy", severity: "error" });
    }
  };


  // Start editing by row id (unique)
  const startEdit = (id) => {
    console.log("startEdit", id);
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingId(id);
    setEditMessageValue(row.message || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditMessageValue("");
  };

  // Add new message handler: delegate to prop if provided, otherwise create a local client-only row
  const handleAddNewMessage = () => {
    setShowCreate(true);
  };

  // Called when CreateNewCampaignMessage finishes creating a message on the server
  const handleCreateSuccess = async (result) => {
    // result is expected to contain { message: 'Created', id: newId }
    setShowCreate(false);
    // reload messages from server
    try {
      setIsLoading(true);
      const msgs = await getMessages();
      const mapped = Array.isArray(msgs)
        ? msgs.map((m) => ({
            dpd: m.dpd || m.DPD || m.typeValue || "",
            message: m.Description || m.message || "",
            id: m.id || m.ID || null,
            templateName: m.TemplateName || m.templateName || "",
            desc_count: m.desc_count != null ? m.desc_count : m.CharacterCount || 0,
            language: m.Language || m.language || m.lang || "",
            ...m,
          }))
        : [];
      setAllRows(mapped);
      setRows(mapped);
      setSnackbar({ open: true, message: "Message created", severity: "success" });
    } catch (err) {
      console.error("Failed to reload messages after create", err);
      setSnackbar({ open: true, message: "Failed to refresh messages", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Save edit by id
  const saveEdit = async (id) => {
    const idx = rows.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const updated = [...rows];
    updated[idx] = { ...updated[idx], message: editMessageValue };
    if (id && id > 0) {
      try {
        setIsLoading(true);
        await updateMessageById(id, editMessageValue);
        setRows(updated);
        setSnackbar({ open: true, message: "Message updated", severity: "success" });
      } catch (err) {
        console.error("Failed to update message:", err);
        setSnackbar({ open: true, message: "Failed to update message", severity: "error" });
      } finally {
        setIsLoading(false);
      }
    } else {
      // client-only row, just update locally
      setRows(updated);
      setSnackbar({ open: true, message: "Message updated", severity: "success" });
    }

    setEditingId(null);
    setEditMessageValue("");
  };

  // Apply combined filters from dropdowns; callers should pass the NEW values to avoid setState asynchronicity
  const applyFilters = ({ dpd = selectedDpd, channel = selectedChannel, template = selectedTemplate, state = selectedState, language = selectedLanguage, fromDba = selectedFromDba } = {}) => {
    let filtered = allRows || [];
    if (dpd && dpd !== "All") filtered = filtered.filter((r) => (r.dpd || "").toString() === dpd);
    if (channel && channel !== "All") filtered = filtered.filter((r) => ((r.Channel || r.channel || r.typeValue || "").toString() === channel));
    if (template && template !== "All") filtered = filtered.filter((r) => ((r.TemplateName || r.templateName || "").toString() === template));
    if (state && state !== "All") filtered = filtered.filter((r) => ((r.State || r.state || "").toString() === state));
    if (language && language !== "All") filtered = filtered.filter((r) => ((r.Language || r.language || r.lang || "").toString() === language));
    if (fromDba && fromDba !== "All") filtered = filtered.filter((r) => ((r.FromDBAName || r.fromDbaName || "").toString() === fromDba));
    setRows(filtered);
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {isLoading && <ProgressOverlay />}
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCreate && (
              <IconButton size="small" onClick={() => setShowCreate(false)}>
                <ArrowCircleLeftOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            <Box component="span">{showCreate ? "Create new message" : "Messages"}</Box>
          </Box>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {showCreate ? (
          <Box>
            <CreateNewCampaignMessage onCreate={handleCreateSuccess} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, mt: 2 }}>
            <Grid item xs={12}>
              <Grid container spacing={1} alignItems="center">
                <Grid item>
                  <Select
                    size="small"
                    value={selectedDpd}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedDpd(v);
                      applyFilters({ dpd: v, channel: selectedChannel, template: selectedTemplate, state: selectedState, language: selectedLanguage, fromDba: selectedFromDba });
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="All">All DPDs</MenuItem>
                    {dpdOptions.map((d) => (
                      <MenuItem key={d} value={d}>{d}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item>
                  <Select
                    size="small"
                    value={selectedChannel}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedChannel(v);
                      applyFilters({ dpd: selectedDpd, channel: v, template: selectedTemplate, state: selectedState, language: selectedLanguage, fromDba: selectedFromDba });
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="All">All Channels</MenuItem>
                    {channelOptions.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item>
                  <Select
                    size="small"
                    value={selectedTemplate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedTemplate(v);
                      applyFilters({ dpd: selectedDpd, channel: selectedChannel, template: v, state: selectedState, language: selectedLanguage, fromDba: selectedFromDba });
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="All">All Templates</MenuItem>
                    {templateOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item>
                  <Select
                    size="small"
                    value={selectedState}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedState(v);
                      applyFilters({ dpd: selectedDpd, channel: selectedChannel, template: selectedTemplate, state: v, language: selectedLanguage, fromDba: selectedFromDba });
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="All">All States</MenuItem>
                    {stateOptions.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item>
                  <Select
                    size="small"
                    value={selectedLanguage}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedLanguage(v);
                      applyFilters({ dpd: selectedDpd, channel: selectedChannel, template: selectedTemplate, state: selectedState, language: v, fromDba: selectedFromDba });
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="All">All Languages</MenuItem>
                    {languageOptions.map((l) => (
                      <MenuItem key={l} value={l}>{l}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item>
                  <Select
                    size="small"
                    value={selectedFromDba}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedFromDba(v);
                      applyFilters({ dpd: selectedDpd, channel: selectedChannel, template: selectedTemplate, state: selectedState, language: selectedLanguage, fromDba: v });
                    }}
                    style={{ minWidth: 160 }}
                  >
                    <MenuItem value="All">All FromDBA</MenuItem>
                    {fromDbaOptions.map((f) => (
                      <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleAddNewMessage()}
                  >
                    Add New Message
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}

        {!showCreate && (
    <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%', whiteSpace: 'nowrap' }}>Template</TableCell>
              <TableCell sx={{ width: '70%', whiteSpace: 'nowrap' }}>Message</TableCell>
              <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>Count</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ width: '10%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.templateName}</TableCell>
                <TableCell sx={{ width: '70%', wordBreak: "break-word" }}>
                  {editingId === row.id ? (
                    <TextField
                      value={editMessageValue}
                      onChange={(e) => setEditMessageValue(e.target.value)}
                      size="small"
                      fullWidth
                    />
                  ) : (
                    row.message
                  )}
                </TableCell>
                <TableCell sx={{ width: '15%', whiteSpace: 'nowrap'}}>{row.desc_count}</TableCell>
                <TableCell align="right">
                  {editingId === row.id ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" onClick={() => saveEdit(row.id)}>
                        Save
                      </Button>
                      <Button size="small" onClick={cancelEdit}>
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Copy message">
                        <IconButton
                          onClick={() => handleCopy(row.message)}
                          size="small"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => startEdit(row.id)}
                          size="small"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      </DialogContent>


      {!showCreate && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Dialog>
  );
};

export default AddLibraryModal;
